/**
 * LogSubmittalReviewModal — log a review pass on a submittal.
 *
 * Creates a new submittal_revisions row (or updates Rev 0 if it has no dates
 * yet and is the current revision). After saving, exposes a "Generate
 * Transmittal PDF" button that calls services/pdf.js → aldercortex-pdf →
 * Supabase Storage, then writes the URL back to the revision row.
 *
 * Opens via CustomEvent('open-log-submittal-review', { detail }):
 *   {
 *     submittal: { ...submittalRow },    // full Supabase submittals row
 *     revisionId?: string,               // if set, updates this revision
 *     projectName?: string,              // for the PDF header
 *     projectNumber?: string,
 *   }
 */

import { useState, useEffect, useRef } from 'react'
import {
  createSubmittalRevision, updateSubmittalRevision, getSubmittalRevisions, updateSubmittal,
  SUBMITTAL_DISPOSITIONS, SUBMITTAL_REVIEWING_PARTIES, submittalLabel,
} from '../services/submittals.js'
import { uploadRevisionFile } from '../services/submittalFiles.js'
import { closeThread, createThreadActivity } from '../services/threads.js'

// Dispositions that indicate the review cycle is complete (no resubmittal expected).
export const FINAL_DISPOSITIONS = new Set(['No Exceptions Taken', 'Not Accepted'])
// Dispositions that require the GC to resubmit.
export const RESUBMIT_DISPOSITIONS = new Set(['Revise and Resubmit', 'Submit Requested Items Only'])
import { generateSubmittalTransmittal } from '../services/pdf.js'
import MarkdownField from './MarkdownField.jsx'

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }
const chip = on => ({ padding: '4px 8px', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 11, cursor: 'pointer', border: '0.5px solid ' + (on ? '#07272D' : 'var(--aj-border-mid)'), background: on ? '#07272D' : 'var(--aj-bg-elevated)', color: on ? '#fff' : 'var(--aj-text)' })

export default function LogSubmittalReviewModal() {
  const [open, setOpen]               = useState(false)
  const [submittal, setSubmittal]     = useState(null)
  const [editRevisionId, setEditRevisionId] = useState(null)
  const [revisionNumber, setRevisionNumber] = useState(0)
  const [projectName, setProjectName] = useState('')
  const [projectNumber, setProjectNumber] = useState('')

  // Form state
  const [disp, setDisp]               = useState([])
  const [comments, setComments]       = useState('')
  const [received, setReceived]       = useState('')
  const [receivedLocked, setReceivedLocked] = useState(false)  // received-from-GC is read-only once pre-filled
  const [sent, setSent]               = useState('')
  const [receivedBack, setReceivedBack] = useState('')
  const [returned, setReturned]       = useState('')
  const [files, setFiles]             = useState([])           // { file, status: 'pending'|'done'|'error' }
  const fileRef = useRef(null)
  const dirtyRef   = useRef(false)  // live "unsaved input" flag read by Escape (avoids stale closure)
  const closingRef = useRef(false)  // prevents double-invocation of handleClose

  // Post-save state
  const [savedRevision, setSavedRevision] = useState(null)
  const [pdfBusy, setPdfBusy]         = useState(false)
  const [pdfUrl, setPdfUrl]           = useState(null)
  const [pdfError, setPdfError]       = useState(null)

  // Signer (current logged-in user) — populated from ctx when the modal opens
  const [signerName, setSignerName]     = useState('')
  const [signatureUrl, setSignatureUrl] = useState(null)
  const [hasSig, setHasSig]             = useState(true)  // assume true to suppress false warnings

  const [submitting, setSubmitting]   = useState(false)
  const [error, setError]             = useState(null)
  const [threadPrompt, setThreadPrompt] = useState(null) // 'close' | null

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      const s = d.submittal || {}
      setSubmittal(s)
      setEditRevisionId(d.revisionId || null)
      setRevisionNumber(d.revisionNumber != null ? d.revisionNumber : 0)
      setProjectName(d.projectName || '')
      setProjectNumber(d.projectNumber || '')
      // When editing a specific revision's review (esp. a draft), prefer that revision's own
      // review fields; otherwise fall back to the submittal's current values.
      const r = d.review || null
      const src = r || s
      setDisp(Array.isArray(src.disposition) ? src.disposition : [])
      setComments(src.review_comments || '')
      // Received from GC is recorded at receipt (resubmittal / revision); reviewers don't edit it.
      // Prefer the revision's received date when reviewing a specific revision; lock it when known.
      const initReceived = d.dateReceived || s.date_received || ''
      setReceived(initReceived)
      setReceivedLocked(!!initReceived)
      setFiles([])
      setSent(src.date_sent || '')
      setReceivedBack(src.date_received_back || '')
      setReturned(src.date_returned || '')
      setSavedRevision(null); setPdfUrl(null); setPdfError(null)
      setThreadPrompt(null)
      setError(null); setSubmitting(false); closingRef.current = false
      // Resolve current member's signature for the transmittal
      import('../outline/ctx.js').then(({ ctx }) => {
        const m = ctx.currentMember
        const name = m?.fields?.['Member Name'] || ''
        const sigs = m?.fields?.['Signature']
        const url = Array.isArray(sigs) && sigs.length ? sigs[0].url : null
        setSignerName(name); setSignatureUrl(url); setHasSig(!!url)
      }).catch(() => {})
      setOpen(true)
    }
    window.addEventListener('open-log-submittal-review', onOpen)
    return () => window.removeEventListener('open-log-submittal-review', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open && !pdfBusy && !dirtyRef.current) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, pdfBusy])

  const toggle = (val, setter) => setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val])

  function addFiles(e) {
    const picked = Array.from(e.target.files || [])
    setFiles(prev => [...prev, ...picked.map(f => ({ file: f, status: 'pending' }))])
    if (fileRef.current) fileRef.current.value = ''
  }
  function removeFile(idx) { setFiles(prev => prev.filter((_, i) => i !== idx)) }

  async function handleSave(asDraft) {
    if (!submittal) return
    const savedAsDraft = !!asDraft
    // Posting requires a disposition; drafts may be incomplete (save-progress).
    if (!savedAsDraft && disp.length === 0) { setError('Select a disposition before posting the review.'); return }
    if (!savedAsDraft && !returned) { setError('Enter the date returned to GC before posting.'); return }
    setSubmitting(true); setError(null)
    const today = new Date().toISOString().slice(0, 10)
    const fields = {
      disposition:      disp.length ? disp : null,
      review_comments:  comments || null,
      date_received:    received || null,
      date_sent:        sent || null,
      date_received_back: receivedBack || null,
      date_returned:    returned || (!savedAsDraft ? today : null) || null,
      is_draft:         savedAsDraft,
    }
    try {
      let rev
      if (editRevisionId) {
        await updateSubmittalRevision(editRevisionId, fields)
        rev = { id: editRevisionId, revision_number: revisionNumber, ...fields }
      } else {
        const existing = await getSubmittalRevisions(submittal.id)
        const rev0 = existing.find(r => r.revision_number === 0)
        const rev0HasDates = rev0 && (rev0.date_received || rev0.date_returned || rev0.date_received_back)
        if (rev0 && !rev0HasDates) { await updateSubmittalRevision(rev0.id, fields); rev = { ...rev0, ...fields } }
        else rev = await createSubmittalRevision(submittal.id, fields)
      }

      // Review markup attachments (kind='review') — distinct from the submitted package.
      if (files.length && rev && rev.id) {
        for (let i = 0; i < files.length; i++) {
          try { await uploadRevisionFile(rev.id, submittal.id, files[i].file, 'review'); setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'done' } : f)) }
          catch { setFiles(prev => prev.map((f, j) => j === i ? { ...f, status: 'error' } : f)) }
        }
      }

      setSubmitting(false)
      const { ctx } = await import('../outline/ctx.js')
      const memberId = ctx.currentMemberId || null
      const revNo = (rev && rev.revision_number != null) ? rev.revision_number : revisionNumber
      const vN = (revNo || 0) + 1
      if (ctx.submittalCache && submittal.contract_id) ctx.submittalCache[submittal.contract_id] = undefined
      if (ctx.submittalRevCache) ctx.submittalRevCache[submittal.id] = undefined

      // Draft: progress saved, no finalize.
      if (savedAsDraft) { window.dispatchEvent(new Event('outline-render')); setOpen(false); return }

      // ── Post ──
      // Auto-generate the transmittal (writes its URL back onto the revision). Non-fatal.
      try {
        await generateSubmittalTransmittal({
          submittalId: submittal.id, revisionId: rev.id, revisionNumber: revNo || 0,
          projectName, projectNumber, submittalNumber: submittal.submittal_number || '', gcNumber: submittal.gc_number || '',
          type: submittal.type || '', specSectionNumber: submittal.spec_section_number || '', specSectionName: submittal.spec_section_name || '',
          reviewer: submittal.reviewing_party || '', disposition: disp, dateReceived: received || null, dateSent: sent || null,
          dateReceivedBack: receivedBack || null, dateReturned: fields.date_returned, reviewComments: comments || '',
          signerName, signatureUrl,
        })
      } catch (_) { /* transmittal best-effort */ }

      // Auto-complete unless the disposition requires a resubmittal or is a straight denial.
      const requiresResubmit = disp.some(d => RESUBMIT_DISPOSITIONS.has(d))
      const isDenial = disp.includes('Not Accepted')
      const complete = !requiresResubmit && !isDenial
      await updateSubmittal(submittal.id, { disposition: disp, status: complete ? 'closed' : 'open' }).catch(() => {})

      // Auto-log a thread activity (template note); close the thread when complete.
      if (submittal.thread_airtable_id) {
        try {
          if (complete) await closeThread(submittal.thread_airtable_id, memberId, 'Submittal review v' + vN + ' posted — ' + disp.join(', ') + '. Submittal complete.')
          else await createThreadActivity(submittal.thread_airtable_id, 'Action Taken', today, 'Submittal review v' + vN + ' posted — ' + disp.join(', ') + '.', memberId)
          if (ctx.caThreadCache) ctx.caThreadCache[submittal.thread_airtable_id] = undefined
        } catch (_) { /* non-fatal */ }
      }
      if (ctx.submittalRevCache) ctx.submittalRevCache[submittal.id] = undefined
      window.dispatchEvent(new Event('outline-render'))
      setOpen(false)
    } catch (ex) {
      setError(ex.message || 'Failed to save review'); setSubmitting(false)
    }
  }

  // Close handler — auto-saves as draft when the user has entered data (X button, Escape).
  // Backdrop clicks are blocked entirely; only explicit actions close this dialog.
  function handleClose() {
    if (pdfBusy || submitting || closingRef.current) return
    closingRef.current = true
    if (dirtyRef.current) {
      // Unsaved input: persist as draft before closing. handleSave(true) calls setOpen(false) on
      // success; on error it shows the message and leaves the dialog open.
      handleSave(true)
        .catch(() => {})
        .finally(() => { closingRef.current = false })
    } else {
      setOpen(false)
      closingRef.current = false
    }
  }

  async function handleGeneratePdf() {
    if (!savedRevision || !submittal) return
    setPdfBusy(true); setPdfError(null); setPdfUrl(null)
    try {
      const result = await generateSubmittalTransmittal({
        submittalId:       submittal.id,
        revisionId:        savedRevision.id,
        revisionNumber:    savedRevision.revision_number ?? 0,
        projectName,
        projectNumber,
        submittalNumber:   submittal.submittal_number || '',
        gcNumber:          submittal.gc_number || '',
        type:              submittal.type || '',
        specSectionNumber: submittal.spec_section_number || '',
        specSectionName:   submittal.spec_section_name || '',
        reviewer:          Array.isArray(submittal.reviewing_party) ? submittal.reviewing_party : (submittal.reviewing_party || ''),
        disposition:       disp,
        dateReceived:      received || null,
        dateSent:          sent || null,
        dateReceivedBack:  receivedBack || null,
        dateReturned:      returned || null,
        reviewComments:    comments || '',
        signerName,
        signatureUrl,
      })
      setPdfUrl(result.url)
    } catch (ex) {
      setPdfError(ex.message || 'PDF generation failed')
    } finally {
      setPdfBusy(false)
    }
  }

  async function handleThreadClose(confirm) {
    const threadId = submittal?.thread_airtable_id
    setThreadPrompt(null)
    if (confirm && threadId) {
      try {
        const { ctx } = await import('../outline/ctx.js')
        await closeThread(threadId, ctx.currentMemberId || null, 'Submittal review complete.')
        if (ctx.caThreadCache) ctx.caThreadCache[threadId] = undefined
        window.dispatchEvent(new Event('outline-render'))
      } catch (_) { /* non-fatal */ }
    }
    setOpen(false)
  }

  if (!open) return null

  // Thread close prompt shown after a final disposition (No Exceptions / Not Accepted)
  if (threadPrompt === 'close') {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div data-modal-id="LogSubmittalReviewModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 400, padding: '20px 20px 16px' }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 8 }}>Close the linked thread?</div>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', marginBottom: 16 }}>
            The disposition indicates the review is complete. Close the linked thread and log a "Thread Closed" activity?
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => handleThreadClose(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>No, leave open</button>
            <button onClick={() => handleThreadClose(true)} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Close Thread</button>
          </div>
        </div>
      </div>
    )
  }

  const hasSaved = !!savedRevision
  // "Review in progress" = unsaved input present. While dirty, a backdrop/outside click (or Escape)
  // must NOT close and discard it — only an explicit Cancel/×/Save/Post closes. (received-from-GC is
  // pre-filled + locked, so it doesn't count as user input.)
  const dirty = !hasSaved && (
    disp.length > 0 || (comments && comments.trim()) || !!sent || !!receivedBack || !!returned ||
    files.length > 0 || (!!received && !receivedLocked)
  )
  dirtyRef.current = dirty

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="LogSubmittalReviewModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 520, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>Log Submittal Review</span>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        {/* Submittal context */}
        {submittal && (
          <div style={{ background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '8px 10px', marginBottom: 16, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>
            {submittalLabel(submittal)}
            {submittal.submittal_number && <span style={{ marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 10 }}>{submittal.submittal_number}</span>}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Disposition */}
          <div>
            <label style={labelStyle}>Disposition</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {SUBMITTAL_DISPOSITIONS.map(p => <button key={p} onClick={() => toggle(p, setDisp)} style={chip(disp.includes(p))}>{p}</button>)}
            </div>
          </div>

          {/* Review Comments */}
          <div>
            <label style={labelStyle}>Review Comments</label>
            <MarkdownField value={comments} onChange={setComments} />
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Received from GC</label>
              <input type="date" disabled={receivedLocked}
                style={{ ...inputStyle, ...(receivedLocked ? { background: 'var(--aj-bg-muted)', color: 'var(--aj-text-mid)', cursor: 'not-allowed' } : {}) }}
                value={received} onChange={e => setReceived(e.target.value)}
                title={receivedLocked ? 'Recorded at receipt — not editable here' : undefined} />
            </div>
            <div>
              <label style={labelStyle}>Sent to reviewer</label>
              <input type="date" style={inputStyle} value={sent} onChange={e => setSent(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Received back</label>
              <input type="date" style={inputStyle} value={receivedBack} onChange={e => setReceivedBack(e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Returned to GC</label>
              <input type="date" style={inputStyle} value={returned} onChange={e => setReturned(e.target.value)} />
            </div>
          </div>

          {/* Review markup / attachments — uploaded to this review round (revision) on save */}
          <div>
            <label style={labelStyle}>Review markup / attachments</label>
            <div style={{ border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '10px 12px', background: 'var(--aj-bg-subtle)' }}>
              {files.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  {files.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid var(--aj-border)' }}>
                      <span style={{ fontSize: 13 }}>📎</span>
                      <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</span>
                      {f.status === 'done' && <span style={{ fontSize: 11, color: '#1D9E75' }}>✓</span>}
                      {f.status === 'error' && <span style={{ fontSize: 11, color: '#c0392b' }}>✕</span>}
                      {f.status === 'pending' && !hasSaved && (
                        <button onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 14, padding: 0 }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!hasSaved && (
                <button onClick={() => fileRef.current?.click()} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add files</button>
              )}
              <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={addFiles} />
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', marginTop: 4 }}>Reviewer markup (PDF) — saved with this review round.</div>
            </div>
          </div>

          {!hasSig && !hasSaved && (
            <div style={{ padding: '6px 10px', background: 'rgba(183,101,26,0.14)', border: '0.5px solid #c79a3a', borderRadius: 4, fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#b8540a' }}>
              No signature on file — the transmittal will use a cursive text signature. Add your signature image in your member profile.
            </div>
          )}
          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            {!hasSaved && (
              <>
                <button onClick={() => setOpen(false)} disabled={submitting} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
                <button onClick={() => handleSave(true)} disabled={submitting} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 500, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: submitting ? 'default' : 'pointer', color: 'var(--aj-text)' }}>
                  {submitting ? 'Saving…' : 'Save draft'}
                </button>
                <button onClick={() => handleSave(false)} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
                  {submitting ? 'Saving…' : 'Post review'}
                </button>
              </>
            )}
            {hasSaved && (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Confirmation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'rgba(26,122,74,0.15)', border: '0.5px solid #1D9E7533', borderRadius: 4 }}>
                  <span style={{ fontSize: 14 }}>✓</span>
                  <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#1D9E75' }}>Review logged successfully.</span>
                </div>

                {/* Generate Transmittal PDF */}
                {!pdfUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={handleGeneratePdf} disabled={pdfBusy} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 500, background: pdfBusy ? 'var(--aj-bg-muted)' : '#07272D', color: pdfBusy ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: pdfBusy ? 'default' : 'pointer' }}>
                      {pdfBusy ? 'Generating PDF…' : 'Generate Transmittal PDF'}
                    </button>
                    {!pdfBusy && <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' }}>optional — creates a formatted transmittal</span>}
                  </div>
                )}
                {pdfError && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>PDF failed: {pdfError}</div>}

                {/* PDF generated */}
                {pdfUrl && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--aj-bg-subtle)', borderRadius: 4 }}>
                    <span style={{ fontSize: 14 }}>📄</span>
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#2563eb', fontWeight: 500 }}>Open Transmittal PDF ↗</a>
                    <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' }}>saved to project</span>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setOpen(false)} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: '#07272D', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
